/**
 * Button Styled Components
 * Styled MUI Button with Material 3 specifications
 */

import type React from 'react';
import { Button as MuiButton, styled } from '@mui/material';
import { ANIMATION_TOKENS, SIZE_CONFIG, CSS_CLASSES } from './constants';
import { getVariantStyles } from './hooks';
import type { ButtonSize, ButtonVariant, SmallPadding } from './types';

// ============================================================================
// STYLED COMPONENT PROPS
// ============================================================================

export interface StyledButtonProps {
  $borderRadius: number | string;
  $morphShape: boolean;
  $selected: boolean;
  $size: ButtonSize;
  $smallPadding: SmallPadding;
  $variant: ButtonVariant;
}

// ============================================================================
// STYLED BUTTON COMPONENT
// ============================================================================

// Explicit annotation avoids TS2742: inferred type crosses deep pnpm package boundaries.
// Cast to a concrete React component type that TypeScript can always name portably.
export type StyledButtonComponent = React.ComponentType<
  React.ComponentPropsWithRef<typeof MuiButton> & StyledButtonProps
>;

export const StyledButton: StyledButtonComponent = styled(MuiButton, {
  shouldForwardProp: (prop) =>
    !['$borderRadius', '$morphShape', '$selected', '$size', '$smallPadding', '$variant'].includes(
      prop as string,
    ),
})<StyledButtonProps>(
  ({ theme, $borderRadius, $morphShape, $selected, $size, $smallPadding, $variant }) => {
    const sizeConfig = SIZE_CONFIG[$size];
    const variantStyles = getVariantStyles(theme, $variant, $selected);

    // Calculate padding
    let horizontalPadding = sizeConfig.paddingX;
    if ($size === 'sm' && $smallPadding === 'deprecated' && sizeConfig.paddingXDeprecated) {
      horizontalPadding = sizeConfig.paddingXDeprecated;
    }

    return {
      // Base styles
      backgroundColor: variantStyles.backgroundColor,
      border: variantStyles.border || 'none',
      borderRadius: typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius,
      boxShadow: variantStyles.boxShadow || 'none',
      color: variantStyles.color,
      fontSize: sizeConfig.fontSize,
      fontWeight: 500,
      height: sizeConfig.height,
      letterSpacing: '0.02em',
      minWidth: sizeConfig.minWidth,
      padding: `0 ${horizontalPadding}px`,
      textTransform: 'none' as const,
      transition: theme.transitions.create(
        ['background-color', 'border-radius', 'box-shadow', 'transform'],
        {
          duration: $morphShape
            ? ANIMATION_TOKENS.duration.medium
            : ANIMATION_TOKENS.duration.short,
          easing: theme.transitions.easing.easeInOut,
        },
      ),

      // Active/pressed state
      '&:active': {
        backgroundColor: variantStyles.activeBackgroundColor,
        transform: $morphShape ? 'scale(0.98)' : undefined,
      },

      // Disabled state
      '&.Mui-disabled': {
        backgroundColor: variantStyles.disabledBackgroundColor,
        border: $variant === 'outlined' ? `1px solid ${theme.palette.action.disabled}` : 'none',
        boxShadow: 'none',
        color: variantStyles.disabledColor,
      },

      // Focus visible
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },

      // Hover state
      '&:hover': {
        backgroundColor: variantStyles.hoverBackgroundColor,
        boxShadow: variantStyles.hoverBoxShadow || variantStyles.boxShadow || 'none',
      },

      // End icon styling
      [`& .${CSS_CLASSES.endIcon}`]: {
        alignItems: 'center',
        display: 'flex',
        marginLeft: sizeConfig.iconGap,
        '& > *': {
          fontSize: sizeConfig.iconSize,
          height: sizeConfig.iconSize,
          width: sizeConfig.iconSize,
        },
      },

      // Start icon styling
      [`& .${CSS_CLASSES.startIcon}`]: {
        alignItems: 'center',
        display: 'flex',
        marginRight: sizeConfig.iconGap,
        '& > *': {
          fontSize: sizeConfig.iconSize,
          height: sizeConfig.iconSize,
          width: sizeConfig.iconSize,
        },
      },
    };
  },
) as StyledButtonComponent;

export default StyledButton;
